/**
 * Player table appearance — avatar pack IDs, display name, colors, seat size.
 * Persisted in D1 users.settings JSON; applied on join + public seat view.
 */

export type AvatarPackId = "cute" | "cartoon" | "funny";

export interface PlayerAppearance {
  /** Catalog id e.g. "cute/01" — maps to /avatars/cute/01.png */
  avatarId: string;
  /** Display name at table (also syncs users.username when saved) */
  username: string;
  /** Hex color for name text */
  nameColor: string;
  /** Hex ring / frame color around avatar */
  frameColor: string;
  /** Seat avatar scale 0.7 – 1.4 */
  avatarScale: number;
  /**
   * Calling Pros badge id (e.g. "phil-ivey") — circle behind seat.
   * Held by Admin AI until claimed on /callingpros.
   */
  proBadgeId?: string;
}

export const DEFAULT_APPEARANCE: PlayerAppearance = {
  avatarId: "cute/01",
  username: "",
  nameColor: "#fbbf24",
  frameColor: "#d4af37",
  avatarScale: 1,
};

export const AVATAR_PACKS: {
  id: AvatarPackId;
  name: string;
  description: string;
}[] = [
  { id: "cute", name: "Cute", description: "CraftPix cute avatars" },
  { id: "cartoon", name: "Cartoon", description: "CraftPix cartoon avatars" },
  { id: "funny", name: "Funny", description: "CraftPix funny vector avatars" },
];

/** Per pack: 01.png … 35.png */
export const AVATARS_PER_PACK = 35;

export function avatarUrl(avatarId: string | undefined | null): string {
  if (!avatarId || !avatarId.includes("/")) {
    return "/avatars/cute/01.png";
  }
  const [pack, file] = avatarId.split("/");
  if (!pack || !file) return "/avatars/cute/01.png";
  const safePack = ["cute", "cartoon", "funny"].includes(pack) ? pack : "cute";
  const base = file.replace(/\.png$/i, "");
  const n = Number(base);
  if (!Number.isFinite(n) || n < 1 || n > AVATARS_PER_PACK) {
    return `/avatars/${safePack}/01.png`;
  }
  const padded = String(Math.floor(n)).padStart(2, "0");
  return `/avatars/${safePack}/${padded}.png`;
}

export function isValidAvatarId(id: string): boolean {
  const m = /^((cute|cartoon|funny)\/(\d{1,2}))$/.exec(id);
  if (!m) return false;
  const n = Number(m[3]);
  return n >= 1 && n <= AVATARS_PER_PACK;
}

export function clampAvatarScale(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.min(1.4, Math.max(0.7, n));
}

export function sanitizeHexColor(c: string, fallback: string): string {
  if (typeof c !== "string") return fallback;
  const t = c.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(t)) return t.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(t)) {
    const r = t[1]!,
      g = t[2]!,
      b = t[3]!;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return fallback;
}

export function sanitizeUsername(raw: string, walletFallback: string): string {
  const s = String(raw || "")
    .trim()
    .replace(/[^\w\s\-_.]/g, "")
    .slice(0, 16);
  if (s.length >= 2) return s;
  return walletFallback.slice(0, 8);
}

export function parseAppearance(
  settingsJson: string | null | undefined,
  username: string,
): PlayerAppearance {
  let parsed: Record<string, unknown> = {};
  if (settingsJson) {
    try {
      parsed = JSON.parse(settingsJson) as Record<string, unknown>;
    } catch {
      parsed = {};
    }
  }
  const app = (parsed.appearance as Record<string, unknown>) || parsed;
  const avatarId = String(
    app.avatarId || parsed.avatarId || DEFAULT_APPEARANCE.avatarId,
  );
  return {
    avatarId: isValidAvatarId(avatarId) ? avatarId : DEFAULT_APPEARANCE.avatarId,
    username: sanitizeUsername(
      String(app.username || parsed.displayName || username || ""),
      username || "Player",
    ),
    nameColor: sanitizeHexColor(
      String(app.nameColor || parsed.nameColor || ""),
      DEFAULT_APPEARANCE.nameColor,
    ),
    frameColor: sanitizeHexColor(
      String(app.frameColor || parsed.frameColor || ""),
      DEFAULT_APPEARANCE.frameColor,
    ),
    avatarScale: clampAvatarScale(
      Number(app.avatarScale ?? parsed.avatarScale ?? 1),
    ),
  };
}

export function listAvatarIds(pack?: AvatarPackId): string[] {
  const packs: AvatarPackId[] = pack
    ? [pack]
    : ["cute", "cartoon", "funny"];
  const out: string[] = [];
  for (const p of packs) {
    for (let i = 1; i <= AVATARS_PER_PACK; i++) {
      out.push(`${p}/${String(i).padStart(2, "0")}`);
    }
  }
  return out;
}
